import './App.css';
import './font.css';
import {useEffect, useState, useCallback, useRef} from "react";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Navbar, {buildNavItems} from "./components/Navbar";
import Hero from "./components/Hero";
import About from "./components/About";
import Skills from "./components/Skills";
import OpenSource from "./components/OpenSource";
import ClosedSource from "./components/ClosedSource";
import Contacts from "./components/Contacts";
import {Backdrop, CursorGlow, HeartShower, Loader, Marquee, MatrixRain, ScrollProgress} from "./components/Effects";
import {PROJECTS_URL, redirectToProjects} from "./utility/ProjectCatalog";
import {useRevealOnScroll} from "./utility/motion";
import Text from "./utility/Text";
import TextENG from "./utility/TextENG";
import Images from "./utility/Images";

const SECTIONS = ['about', 'work', 'contacts'];
const MARQUEE_ITEMS = ['Java', 'Spring Boot', 'React', 'Node.js', 'REST APIs', 'MySQL', 'IBM DB2', 'MongoDB', 'Firebase', 'Google Cloud', 'Docker', 'Git'];

// Hash routes also work when the portfolio is served from a static host.
const readRoute = () => {
    const path = window.location.hash.replace(/^#\/?/, '').split('/')[0];
    if (path === 'projects') return 'projects';
    return SECTIONS.includes(path) ? path : 'about';
};

const scrollOffset = () => (window.innerWidth < 720 ? 16 : 88);

// Konami Code sequence
const KONAMI_CODE = [
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'
];

function App() {
    const [route, setRoute] = useState(readRoute);
    const [checked, setChecked] = useState(() => {
        const saved = localStorage.getItem('language');
        return saved === 'true';
    });
    const [aboutMeText, setAboutMeText] = useState('');
    const [activeSection, setActiveSection] = useState('');
    const [pendingSection, setPendingSection] = useState(null);
    const navigationIntentRef = useRef(null);
    const navigationTimerRef = useRef(null);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Asset preloader
    const [assetsLoaded, setAssetsLoaded] = useState(false);
    const [loadProgress, setLoadProgress] = useState(0);

    useEffect(() => {
        const imageUrls = [Images.memoji];
        const startTime = Date.now();
        const MIN_DISPLAY_MS = 700;
        const total = imageUrls.length + 1; // +1 for fonts
        let loaded = 0;

        const bump = () => {
            loaded += 1;
            setLoadProgress(Math.round((loaded / total) * 100));
        };

        const imagePromises = imageUrls.map(
            (src) =>
                new Promise((resolve) => {
                    const img = new Image();
                    const finish = () => { bump(); resolve(); };
                    img.onload = finish;
                    img.onerror = finish;
                    img.src = src;
                })
        );

        const fontsPromise = (document.fonts && document.fonts.ready
            ? document.fonts.ready
            : Promise.resolve()
        ).then(() => bump());

        Promise.all([...imagePromises, fontsPromise]).then(() => {
            const elapsed = Date.now() - startTime;
            const wait = Math.max(0, MIN_DISPLAY_MS - elapsed);
            setTimeout(() => setAssetsLoaded(true), wait);
        });
    }, []);

    // Lock body scroll while loader is visible
    useEffect(() => {
        if (!assetsLoaded) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = prev; };
        }
    }, [assetsLoaded]);

    useRevealOnScroll([assetsLoaded, checked]);

    // Konami code state
    const [konamiIndex, setKonamiIndex] = useState(0);
    const [matrixActive, setMatrixActive] = useState(false);

    // Secret word easter egg state
    const [secretBuffer, setSecretBuffer] = useState('');
    const [secretToast, setSecretToast] = useState('');

    // Footer heart easter egg
    const [heartShower, setHeartShower] = useState(false);

    // Console ASCII art on mount
    useEffect(() => {
        console.log(
            '%c' +
            '  _____ ____  \n' +
            ' |  ___/ ___| \n' +
            ' | |_ | |     \n' +
            ' |  _|| |___  \n' +
            ' |_|   \\____| \n' +
            '\n' +
            ' Hey developer! Nice to see you here 👋\n' +
            ' Check out my GitHub: github.com/carellice\n',
            'color: #fb7185; font-family: monospace; font-size: 14px; font-weight: bold;'
        );
    }, []);

    // Scroll handler
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 600);
            setScrolled(window.scrollY > 24);

            // Keep the selection on the requested destination while smooth scrolling.
            const requestedSection = navigationIntentRef.current;
            if (requestedSection) {
                const target = document.getElementById(requestedSection);
                const offset = scrollOffset();
                const targetTop = target?.getBoundingClientRect().top;
                const reachedBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
                const hasReachedTarget = typeof targetTop === 'number'
                    && ((targetTop >= offset - 12 && targetTop <= offset + 24) || reachedBottom);

                if (hasReachedTarget) {
                    setActiveSection(requestedSection);
                    setPendingSection(null);
                    navigationIntentRef.current = null;
                }
                return;
            }

            // Detect active section during manual scrolling.
            for (const id of [...SECTIONS].reverse()) {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top <= window.innerHeight * 0.4) {
                    setActiveSection(id);
                    break;
                }
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const syncRoute = () => setRoute(readRoute());
        window.addEventListener('hashchange', syncRoute);
        window.addEventListener('popstate', syncRoute);
        return () => {
            window.removeEventListener('hashchange', syncRoute);
            window.removeEventListener('popstate', syncRoute);
        };
    }, []);

    useEffect(() => {
        document.title = 'Flavio Ceccarelli · Portfolio';
        document.documentElement.lang = checked ? 'it' : 'en';
    }, [checked]);

    useEffect(() => {
        if (route === 'projects') {
            redirectToProjects();
            return;
        }
        if (navigationIntentRef.current) return;
        setActiveSection(route);
        setPendingSection(null);
        const element = document.getElementById(route);
        const top = route !== 'about' && element
            ? element.getBoundingClientRect().top + window.pageYOffset - scrollOffset()
            : 0;
        window.scrollTo({ top, behavior: 'auto' });
    }, [route]);

    // Matrix rain effect
    const triggerMatrixRain = useCallback(() => {
        setMatrixActive(true);
        setTimeout(() => setMatrixActive(false), 6000);
    }, []);

    // Konami code listener
    useEffect(() => {
        const handleKeyDown = (e) => {
            const key = e.key;
            if (key === KONAMI_CODE[konamiIndex]) {
                const nextIndex = konamiIndex + 1;
                if (nextIndex === KONAMI_CODE.length) {
                    setKonamiIndex(0);
                    triggerMatrixRain();
                } else {
                    setKonamiIndex(nextIndex);
                }
            } else {
                setKonamiIndex(0);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [konamiIndex, triggerMatrixRain]);

    // Secret word listener ("ciao" or "hello")
    useEffect(() => {
        const handleSecretKey = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            const key = e.key.toLowerCase();
            if (key.length !== 1) return;
            const newBuffer = (secretBuffer + key).slice(-5);
            setSecretBuffer(newBuffer);
            if (newBuffer.endsWith('ciao')) {
                setSecretToast(checked ? 'Ciao! Bello vederti qui! 👋' : 'Ciao! Nice to see you! 👋');
                setTimeout(() => setSecretToast(''), 3000);
                setSecretBuffer('');
            } else if (newBuffer.endsWith('hello')) {
                setSecretToast(checked ? 'Hey! Benvenuto nel mio portfolio! 🚀' : 'Hey! Welcome to my portfolio! 🚀');
                setTimeout(() => setSecretToast(''), 3000);
                setSecretBuffer('');
            }
        };
        window.addEventListener('keypress', handleSecretKey);
        return () => window.removeEventListener('keypress', handleSecretKey);
    }, [secretBuffer, checked]);

    // Heart click handler
    const handleHeartClick = (e) => {
        e.stopPropagation();
        if (heartShower) return;
        setHeartShower(true);
        setTimeout(() => setHeartShower(false), 5500);
    };

    // Process about me text with placeholders
    const processAboutMeText = async (isItalian) => {
        try {
            const age = await Text.calculateAge();
            const textClass = isItalian ? Text : TextENG;

            let processedText = textClass.textAboutMe
                .replace('[anni]', age.toString())
                .replace('[nome]', textClass.nome)
                .replace('[cognome]', textClass.cognome)
                .replace('[professione]', textClass.professione)
                .replace('[hobby]', textClass.hobby)
                .replace('[passioni]', textClass.passioni)
                .replace('[studi]', textClass.studi)
                .replace('[esperienzeLavoroPassate]', textClass.esperienzeLavoroPassate)
                .replace('[lavoroAttuale]', textClass.lavoroAttuale)
                .replace('[altro]', textClass.altro);

            setAboutMeText(processedText);
        } catch (error) {
            console.error('Error processing about me text:', error);
            setAboutMeText('Error loading content...');
        }
    };

    // Update localStorage when language changes
    useEffect(() => {
        localStorage.setItem('language', checked.toString());
        processAboutMeText(checked);
    }, [checked]); // eslint-disable-line react-hooks/exhaustive-deps

    const scrollToSection = (id) => {
        const destination = id === 'about' ? '#/' : `#/${id}`;
        navigationIntentRef.current = id;
        setPendingSection(id);
        setActiveSection(id);
        // Release the lock even if the page cannot scroll all the way to the target.
        clearTimeout(navigationTimerRef.current);
        navigationTimerRef.current = setTimeout(() => {
            if (navigationIntentRef.current === id) {
                navigationIntentRef.current = null;
                setPendingSection(null);
            }
        }, 1400);
        if (window.location.hash !== destination) {
            window.history.pushState({}, '', destination);
            setRoute(id);
        }
        const el = document.getElementById(id);
        if (el) {
            const top = id === 'about' ? 0 : el.getBoundingClientRect().top + window.pageYOffset - scrollOffset();
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    const navigateFromLink = (event, id) => {
        if (id === 'projects') return;
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        scrollToSection(id);
    };

    const navItems = buildNavItems(checked, PROJECTS_URL);
    const navigationSelection = pendingSection || activeSection;
    const selectedNavId = navItems.some((item) => item.id === navigationSelection)
        ? navigationSelection
        : navItems[0].id;

    return (
        <div className={`App ${assetsLoaded ? 'is-ready' : ''}`}>
            <Loader visible={!assetsLoaded} progress={loadProgress} />
            <Backdrop />
            <CursorGlow />
            <ScrollProgress />
            <MatrixRain active={matrixActive} checked={checked} />

            <Navbar
                checked={checked}
                setChecked={setChecked}
                items={navItems}
                selectedId={selectedNavId}
                onNavigate={navigateFromLink}
                scrolled={scrolled}
            />

            <button
                type="button"
                className={`to-top ${showScrollTop ? 'is-visible' : ''}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={checked ? 'Torna su' : 'Back to top'}
                tabIndex={showScrollTop ? 0 : -1}
            >
                <KeyboardArrowUpIcon />
            </button>

            <main id="main-content">
                <Hero checked={checked} onNavigate={navigateFromLink} />
                <Marquee items={MARQUEE_ITEMS} />
                <About content={aboutMeText} checked={checked} />
                <Skills checked={checked} />
                <OpenSource checked={checked} />
                <ClosedSource checked={checked} />
                <Contacts checked={checked} />
            </main>

            {/* Secret Word Toast (Easter Egg) */}
            <div className={`toast ${secretToast ? 'is-visible' : ''}`} role="status">
                {secretToast}
            </div>

            <HeartShower active={heartShower} />

            <footer className="footer">
                <div className="footer__big" aria-hidden="true">Flavio Ceccarelli</div>
                <div className="footer__row">
                    <p className="footer__made">
                        {checked ? 'Fatto con' : 'Made with'}
                        <button
                            type="button"
                            className="footer__heart"
                            onClick={handleHeartClick}
                            aria-label="♥"
                        >
                            <FavoriteIcon />
                        </button>
                        {checked ? 'da' : 'by'}
                        <span className="footer__initials">F.C.</span>
                    </p>
                    <p className="footer__copy mono">&copy; {new Date().getFullYear()} Flavio Ceccarelli</p>
                </div>
            </footer>
        </div>
    );
}

export default App;
